import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, RotateCcw, Gift, Loader2, Copy, Check, History, CalendarPlus, Ticket, HelpCircle, X } from "lucide-react";
import { Wheel } from "react-custom-roulette";
import { useCoreController } from "@/services/coreController";
import { apiService } from "@/services/api";
import { analyticsService } from "@/services/analytics";
import { Notification } from "@/components/Notification";
import type { LotterySegment, SpinResult, LotteryHistorySpin, LotteryStatus } from "@/types";
import { copyToClipboard } from "@/lib/utils";
import { storage } from "@/lib/storage";
import { BottomSheet, BottomSheetContent, BottomSheetTitle, BottomSheetDescription } from "@/components/ui/bottom-sheet";

const DEPOSIT_POLL_INTERVAL_MS = 5000;
const DEPOSIT_POLL_MAX_DURATION_MS = 30000;

export function LotteryPage() {
  const navigate = useNavigate();
  const coreController = useCoreController();

  const [segments, setSegments] = useState<LotterySegment[]>([]);
  const [status, setStatus] = useState<LotteryStatus>({
    spinsAvailable: 0,
    canSpin: false,
    dailyClaimedToday: false,
    dailyLimit: 1,
    rechargeSpinsEarnedToday: 0,
    rechargeSpinsLimit: 3,
    totalLimit: 10,
  });
  const [spinsAvailable, setSpinsAvailable] = useState(0);
  const [mustSpin, setMustSpin] = useState(false);
  const [prizeNumber, setPrizeNumber] = useState(0);
  const [lastResult, setLastResult] = useState<SpinResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isClaimingDaily, setIsClaimingDaily] = useState(false);
  const [history, setHistory] = useState<LotteryHistorySpin[]>([]);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [showDepositBonusBanner, setShowDepositBonusBanner] = useState(false);
  const [showRulesSheet, setShowRulesSheet] = useState(false);
  const [isPollingDeposit, setIsPollingDeposit] = useState(false);
  const pollTimeoutRef = useRef<number | null>(null);
  const pollIntervalRef = useRef<number | null>(null);
  const initialSpinsRef = useRef<number | null>(null);
  const [notification, setNotification] = useState<{
    isVisible: boolean;
    message: string;
    type: "error" | "success";
  }>({ isVisible: false, message: "", type: "error" });

  const showNotification = useCallback((message: string, type: "error" | "success" = "error") => {
    setNotification({ isVisible: true, message, type });
  }, []);

  const hideNotification = useCallback(() => {
    setNotification((prev) => ({ ...prev, isVisible: false }));
  }, []);

  // Convertir LotterySegment[] en WheelData[] pour react-custom-roulette
  const wheelData = segments.map((s) => ({
    option: `${s.icon ?? ''} ${s.label}`.trim(),
    style: {
      backgroundColor: s.color,
      textColor: '#ffffff',
    },
  }));

  const loadStatus = useCallback(async (options?: { fromDeposit?: boolean }) => {
    try {
      const newStatus = await apiService.getLotteryStatus();
      setStatus(newStatus);
      setSpinsAvailable(newStatus.spinsAvailable);

      if (options?.fromDeposit && initialSpinsRef.current !== null) {
        if (newStatus.spinsAvailable > initialSpinsRef.current) {
          setShowDepositBonusBanner(true);
          stopDepositPolling();
        }
      }
    } catch {
      // Silent: status errors are already handled by the service fallback
    }
  }, []);

  const refreshHistory = useCallback(async () => {
    try {
      const historyData = await apiService.getLotteryHistory();
      setHistory(historyData.spins);
    } catch {
      // Silent fallback
    }
  }, []);

  const stopDepositPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      window.clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (pollTimeoutRef.current) {
      window.clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
    setIsPollingDeposit(false);
  }, []);

  const startDepositPolling = useCallback(() => {
    stopDepositPolling();
    initialSpinsRef.current = spinsAvailable;
    setIsPollingDeposit(true);

    // Delayed first check so the worker has time to process the deposit
    const initialDelay = window.setTimeout(async () => {
      await loadStatus({ fromDeposit: true });

      pollIntervalRef.current = window.setInterval(() => {
        loadStatus({ fromDeposit: true });
      }, DEPOSIT_POLL_INTERVAL_MS);

      // Hard stop after max duration from the first successful poll
      pollTimeoutRef.current = window.setTimeout(() => {
        stopDepositPolling();
      }, DEPOSIT_POLL_MAX_DURATION_MS - DEPOSIT_POLL_INTERVAL_MS);
    }, DEPOSIT_POLL_INTERVAL_MS);

    // Cleanup guard in case component unmounts before the timeout fires
    pollTimeoutRef.current = initialDelay;
  }, [spinsAvailable, loadStatus, stopDepositPolling]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: showNotification is stable via useCallback
  useEffect(() => {
    analyticsService.trackScreenView("lottery_page");

    const loadData = async () => {
      try {
        const [config, newStatus, historyData] = await Promise.all([
          apiService.getLotteryWheelConfig(),
          apiService.getLotteryStatus(),
          apiService.getLotteryHistory(),
        ]);
        setSegments(config.segments);
        setStatus(newStatus);
        setSpinsAvailable(newStatus.spinsAvailable);
        setHistory(historyData.spins);
      } catch (error) {
        showNotification("Erreur lors du chargement");
      } finally {
        setIsLoading(false);
      }
    };

    loadData();

    return () => {
      stopDepositPolling();
    };
  }, [showNotification, stopDepositPolling]);

  // Silent daily claim probe on mount: determines whether the daily spin is already
  // claimed without showing any error. Runs only when no spins are available and the
  // daily status is not already known to be claimed.
  useEffect(() => {
    if (isLoading || spinsAvailable > 0 || status.dailyClaimedToday) return;

    let cancelled = false;

    const probeDaily = async () => {
      try {
        const result = await apiService.claimDailyLotterySpin();
        if (cancelled) return;

        if (result.success) {
          setSpinsAvailable(result.spinsAvailable);
          setStatus((prev) => ({
            ...prev,
            spinsAvailable: result.spinsAvailable,
            dailyClaimedToday: false,
          }));
        } else {
          setStatus((prev) => ({
            ...prev,
            spinsAvailable: result.spinsAvailable,
            dailyClaimedToday: result.alreadyClaimedToday,
          }));
        }
      } catch {
        // Silent: do not notify the user
      }
    };

    probeDaily();

    return () => {
      cancelled = true;
    };
  }, [isLoading]);

  // Start deposit polling if the user arrives with a pending deposit flag
  useEffect(() => {
    if (isLoading) return;

    const pendingDeposit = storage.read<{ amount: number; ts: number }>("pending_deposit_lottery_bonus");
    if (!pendingDeposit) return;

    const isRecent = Date.now() - pendingDeposit.ts < 10 * 60 * 1000;
    if (isRecent) {
      startDepositPolling();
    }

    storage.remove("pending_deposit_lottery_bonus");
  }, [isLoading, startDepositPolling]);

  const handleClaimDaily = async () => {
    if (isClaimingDaily) return;

    setIsClaimingDaily(true);
    try {
      const result = await apiService.claimDailyLotterySpin();

      if (result.success) {
        setSpinsAvailable(result.spinsAvailable);
        setStatus((prev) => ({ ...prev, spinsAvailable: result.spinsAvailable }));
        showNotification("🎁 Tour quotidien réclamé !", "success");
        analyticsService.trackEvent("lottery_claim_daily", { success: true });
      } else if (result.alreadyClaimedToday) {
        setSpinsAvailable(result.spinsAvailable);
        setStatus((prev) => ({ ...prev, spinsAvailable: result.spinsAvailable, dailyClaimedToday: true }));
        showNotification("Tour quotidien déjà réclamé aujourd'hui");
      } else {
        setSpinsAvailable(result.spinsAvailable);
        setStatus((prev) => ({ ...prev, spinsAvailable: result.spinsAvailable }));
        showNotification(result.error || "Erreur lors de la réclamation");
      }
    } catch (error: any) {
      showNotification("Erreur lors de la réclamation");
      analyticsService.trackError(error, "lottery_claim_daily");
    } finally {
      setIsClaimingDaily(false);
    }
  };

  const handleSpin = async () => {
    if (isProcessing || mustSpin || spinsAvailable <= 0) return;

    setIsProcessing(true);
    setLastResult(null);

    try {
      // Rafraîchir la config de la roue immédiatement avant le tirage pour éviter
      // d'afficher un segment stale (par ex. la roue s'arrête sur 1500F alors que
      // le coupon gagné est 500F).
      const freshConfig = await apiService.getLotteryWheelConfig();
      const freshSegments = freshConfig.segments;
      setSegments(freshSegments);

      const result = await apiService.lotterySpin();

      if (!result.success) {
        showNotification(result.error || "Erreur lors du tirage");
        setIsProcessing(false);
        return;
      }

      let targetIndex = result.segmentIndex ?? 0;

      // Vérifier que l'index renvoyé par le backend correspond bien au rewardValue.
      // Si la config a changé entre le chargement initial et le spin, l'index peut
      // pointer sur le mauvais segment ; on le corrige ici pour que la roue s'arrête
      // sur le bon label.
      const segmentAtIndex = freshSegments[targetIndex];
      const indexOutOfBounds = targetIndex < 0 || targetIndex >= freshSegments.length;
      const valueMismatch =
        result.resultType === "win-reload" &&
        segmentAtIndex?.rewardValue !== result.rewardValue;

      if (indexOutOfBounds || valueMismatch) {
        const correctedIndex = freshSegments.findIndex(
          (s) =>
            s.type === result.resultType &&
            (result.resultType !== "win-reload" || s.rewardValue === result.rewardValue)
        );

        if (correctedIndex !== -1) {
          console.warn(
            `[lottery] segmentIndex mismatch: backend=${targetIndex} (${segmentAtIndex?.label ?? "?"}), rewardValue=${result.rewardValue}. Using correctedIndex=${correctedIndex} (${freshSegments[correctedIndex]?.label})`
          );
          targetIndex = correctedIndex;
        }
      }

      if (freshSegments.length > 0) {
        setPrizeNumber(targetIndex);
        setMustSpin(true);
      }

      setSpinsAvailable(result.spinsRemaining ?? 0);
      setStatus((prev) => ({ ...prev, spinsAvailable: result.spinsRemaining ?? 0 }));
      setLastResult(result);

      analyticsService.trackEvent("lottery_spin", {
        resultType: result.resultType,
        rewardValue: result.rewardValue,
        segmentIndex: targetIndex,
      });
    } catch (error: any) {
      showNotification("Erreur lors du tirage");
      setIsProcessing(false);
      analyticsService.trackError(error, "lottery_spin");
    }
  };

  const handleStopSpinning = () => {
    setMustSpin(false);
    setIsProcessing(false);

    if (!lastResult) return;

    if (lastResult.resultType === "win-reload") {
      showNotification(
        `🎉 Vous avez gagné ${lastResult.rewardValue?.toLocaleString()} FCFA ! Code: ${lastResult.couponCode}`,
        "success"
      );
    } else if (lastResult.resultType === "replay") {
      showNotification("🔄 Relance ! Vous pouvez rejouer.", "success");
    } else {
      showNotification("Perdu cette fois. Retentez votre chance !");
    }

    apiService.getLotteryHistory().then((data) => {
      if (data.spins.length > 0) setHistory(data.spins);
    });

    loadStatus();
  };

  const navigateToCoupon = (code?: string) => {
    navigate("/recharge-coupon", { state: { prefilledCode: code } });
  };

  const handleCopy = async (code: string, spinId: string) => {
    const ok = await copyToClipboard(code);
    if (ok) {
      setCopiedId(spinId);
      setTimeout(() => setCopiedId(null), 2000);
    } else {
      showNotification("Impossible de copier");
    }
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (isLoading) {
    return (
      <div className="relative flex min-h-[100dvh] flex-col items-center justify-center bg-background text-foreground [padding-top:env(safe-area-inset-top)]">
        <div
          aria-hidden
          className="pointer-events-none fixed inset-0 -z-10"
          style={{ background: "radial-gradient(120% 80% at 50% 0%, var(--page-gradient-via) 0%, var(--page-gradient-from) 45%, var(--page-gradient-to) 100%)" }}
        />
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
        <p className="mt-3 text-sm text-muted-foreground">Chargement…</p>
      </div>
    );
  }

  if (segments.length === 0) {
    return (
      <div className="relative flex min-h-[100dvh] flex-col items-center justify-center bg-background text-foreground [padding-top:env(safe-area-inset-top)]">
        <div
          aria-hidden
          className="pointer-events-none fixed inset-0 -z-10"
          style={{ background: "radial-gradient(120% 80% at 50% 0%, var(--page-gradient-via) 0%, var(--page-gradient-from) 45%, var(--page-gradient-to) 100%)" }}
        />
        <Gift className="size-12 text-primary mx-auto mb-3" />
        <p className="text-muted-foreground">La roue n'est pas encore configurée.</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="relative flex min-h-[100dvh] flex-col bg-background text-foreground [padding-top:env(safe-area-inset-top)] [padding-bottom:env(safe-area-inset-bottom)]"
    >
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10"
        style={{ background: "radial-gradient(120% 80% at 50% 0%, var(--page-gradient-via) 0%, var(--page-gradient-from) 45%, var(--page-gradient-to) 100%)" }}
      />

      {/* App Bar */}
      <header className="sticky top-0 z-30 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[52ch] items-center gap-3 px-4 py-3 lg:max-w-6xl lg:px-8">
          <button
            type="button"
            onClick={() => navigate(-1)}
            aria-label="Retour"
            className="grid size-9 place-items-center rounded-full bg-muted ring-1 ring-border"
          >
            <ArrowLeft className="size-4" />
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-base font-semibold tracking-tight text-foreground">Roue de la Chance</h1>
            <p className="truncate text-[10px] uppercase tracking-widest text-muted-foreground">Tente ta chance</p>
        <p className="truncate text-[10px] text-muted-foreground">
          Config: {segments.map((s) => s.label).join(" | ")}
        </p>
          </div>
          <button
            type="button"
            onClick={() => setShowRulesSheet(true)}
            aria-label="Comment ça fonctionne"
            className="grid size-9 place-items-center rounded-full bg-muted ring-1 ring-border"
          >
            <HelpCircle className="size-4" />
          </button>
        </div>
      </header>

      <div className="mx-auto w-full max-w-[52ch] px-4 py-6 lg:max-w-6xl lg:px-8">
        {/* Bannière tour offert après dépôt */}
        {showDepositBonusBanner && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 rounded-xl border border-primary/40 bg-primary/15 p-3 text-center"
            >
            <p className="text-sm font-semibold text-foreground">
             🎁 Tu as gagné un tour gratuit grâce à ton dépôt !
            </p>
          </motion.div>
        )}

        {/* Polling indicator */}
        {isPollingDeposit && !showDepositBonusBanner && (
          <div className="mb-4 flex items-center justify-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="size-3.5 animate-spin" />
            <span>Vérification du tour offert...</span>
          </div>
        )}

        {/* Tours restants */}
        <div className="mb-6 text-center">
          <span className="text-sm text-muted-foreground">Tours disponibles</span>
          <p className="text-3xl font-bold text-primary">{spinsAvailable}</p>
          <p className="text-xs text-muted-foreground">
            sur {status.totalLimit ?? 10} max
          </p>
        </div>

        {/* Limites */}
        <div className="mb-4 grid grid-cols-2 gap-2 rounded-xl border border-border bg-muted p-3">
          <div className="text-center">
            <p className="text-xs text-muted-foreground">Tour quotidien</p>
            <p className="text-sm font-semibold text-foreground">
              {status.dailyClaimedToday ? "1/1" : "0/1"}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-muted-foreground">Recharges ≥ 1000 F</p>
            <p className="text-sm font-semibold text-foreground">
              {status.rechargeSpinsEarnedToday ?? 0}/{status.rechargeSpinsLimit ?? 3}
            </p>
          </div>
        </div>

        {/* Roue */}
        <div className="relative mx-auto mb-6">
          <div style={{
            transform: 'scale(0.78)',
            transformOrigin: 'top center',
            width: 'fit-content',
            margin: '0 auto',
            marginBottom: '-65px',
            pointerEvents: 'none',
          }}>
            <Wheel
              mustStartSpinning={mustSpin}
              prizeNumber={prizeNumber}
              data={wheelData}
              backgroundColors={segments.map((s) => s.color)}
              textColors={['#ffffff']}
              outerBorderColor="#c084fc"
              outerBorderWidth={2}
              fontSize={24}
              perpendicularText={false}
              textDistance={60}
              spinDuration={0.8}
              onStopSpinning={handleStopSpinning}
            />
          </div>
          {isProcessing && !mustSpin && (
            <div className="absolute inset-0 z-10 flex items-center justify-center">
              <div className="rounded-full bg-muted p-4 ring-1 ring-border backdrop-blur">
                <Loader2 className="size-10 animate-spin text-primary" />
              </div>
            </div>
          )}
        </div>

        {/* Bouton principal : tourner ou réclamer quotidien */}
        <motion.button
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          onClick={spinsAvailable > 0 ? handleSpin : handleClaimDaily}
          disabled={isProcessing || isClaimingDaily}
          className="relative z-20 flex w-full items-center justify-center gap-2 rounded-2xl bg-primary py-3 text-sm font-semibold text-primary-foreground shadow-lg transition active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isProcessing ? (
            <>
              <Loader2 className="size-5 animate-spin" />
              <span>Tirage en cours...</span>
            </>
          ) : isClaimingDaily ? (
            <>
              <Loader2 className="size-5 animate-spin" />
              <span>Réclamation en cours...</span>
            </>
          ) : spinsAvailable > 0 ? (
            <>
              <RotateCcw className="size-5" />
              <span>Tourner la roue</span>
            </>
          ) : status.dailyClaimedToday ? (
            <>
              <Gift className="size-5" />
              <span>Recharge ≥ 1000 F pour gagner un tour</span>
            </>
          ) : (
            <>
              <CalendarPlus className="size-5" />
              <span>Réclamer mon tour quotidien</span>
            </>
          )}
        </motion.button>

        {/* Message sous le bouton quand le tour quotidien est déjà pris */}
        {spinsAvailable === 0 && status.dailyClaimedToday && (
          <div className="mt-3 text-center">
            <p className="text-xs text-muted-foreground">
              Tu as déjà réclamé ton tour quotidien aujourd'hui.
            </p>
            <p className="text-xs text-muted-foreground">
              Reviens demain ou fais une recharge ≥ 1000 F pour gagner un tour.
            </p>
          </div>
        )}

        {/* Dernier résultat */}
        {lastResult && !mustSpin && (
          <motion.div
            initial={{ opacity: 0, scale: 0.5, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
            className="mt-6 rounded-2xl p-5 text-center shadow-lg border-2"
            style={{
              background: lastResult.resultType === "win-reload"
                ? "linear-gradient(135deg, #fbbf24, #f59e0b)"
                : lastResult.resultType === "replay"
                  ? "linear-gradient(135deg, #a78bfa, #7c3aed)"
                  : "linear-gradient(135deg, #9ca3af, #6b7280)",
              borderColor: lastResult.resultType === "win-reload"
                ? "#fde68a"
                : lastResult.resultType === "replay"
                  ? "#c4b5fd"
                  : "#d1d5db",
            }}
          >
            <p className="text-xl font-extrabold text-foreground drop-shadow-md">
              {lastResult.resultType === "win-reload" && `🎉 +${lastResult.rewardValue?.toLocaleString()} FCFA !`}
              {lastResult.resultType === "replay" && "🔄 Tour offert !"}
              {lastResult.resultType === "loss" && "😢 Perdu !"}
            </p>
            {lastResult.couponCode && (
              <div className="mt-3 rounded-lg bg-muted px-4 py-2 backdrop-blur-sm">
                <p className="font-mono text-sm font-bold tracking-wider text-foreground">{lastResult.couponCode}</p>
              </div>
            )}

            {lastResult.resultType === "win-reload" && lastResult.couponCode && (
              <button
                type="button"
                onClick={() => navigateToCoupon(lastResult.couponCode)}
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition active:scale-95"
              >
                <Ticket className="size-4" />
                <span>Utiliser mon coupon</span>
              </button>
            )}

            <div className="mt-3 text-center">
              <p className="text-[11px] text-muted-foreground">
                Ou va dans Compte → Coupons de recharge et entre ce code manuellement.
              </p>
            </div>
          </motion.div>
        )}

        {/* Info */}
        <div className="mt-6 rounded-xl border border-border bg-muted p-4">
          <div className="flex gap-3">
            <Gift className="mt-0.5 size-5 shrink-0 text-primary" />
            <p className="text-xs text-muted-foreground">
              Gagnez des tours en effectuant des recharges. Chaque tour vous donne une chance de gagner un des lots
            </p>
          </div>
        </div>

        {/* Historique des tours */}
        {history.length > 0 && (
          <div className="mt-6">
            <div className="mb-3 flex items-center gap-2">
              <History className="size-4 text-primary" />
              <h2 className="text-sm font-bold text-foreground">Historique des tours</h2>
            </div>
            <div className="max-h-80 space-y-2 overflow-y-auto">
              {history.map((spin) => (
                <motion.div
                  key={spin.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="rounded-xl bg-muted p-3 ring-1 ring-border"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">
                        {spin.result_type === "win-reload"
                          ? spin.reward_id?.icon ?? "💰"
                          : spin.result_type === "replay"
                            ? spin.reward_id?.icon ?? "🔄"
                            : spin.reward_id?.icon ?? "❌"}
                      </span>
                      <span className="text-sm font-semibold text-foreground">
                        {spin.reward_id?.label ??
                          (spin.result_type === "win-reload"
                            ? `Recharge ${spin.reward_value?.toLocaleString()} FCFA`
                            : spin.result_type === "replay"
                              ? "Relance"
                              : "Perdu")}
                      </span>
                    </div>
                    <span className="text-[10px] text-muted-foreground">
                      {formatDate(spin.date_created)}
                    </span>
                  </div>
                  {spin.result_type === "win-reload" && spin.coupon_id?.code && (
                    <div className="mt-2 flex flex-col gap-2 rounded-lg bg-muted px-3 py-2">
                      <div className="flex items-center justify-between">
                        <code className="font-mono text-xs font-bold tracking-wide text-foreground">
                          {spin.coupon_id.code}
                        </code>
                        <button
                          type="button"
                          onClick={() => handleCopy(spin.coupon_id!.code, spin.id)}
                          className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium transition-all active:scale-95"
                          style={{
                            color: copiedId === spin.id ? "#16a34a" : "var(--color-primary)",
                            backgroundColor: copiedId === spin.id ? "rgba(34,197,94,0.15)" : "var(--color-primary/0.15)",
                          }}
                        >
                          {copiedId === spin.id ? (
                            <>
                              <Check className="size-3.5" />
                              <span>Copié</span>
                            </>
                          ) : (
                            <>
                              <Copy className="size-3.5" />
                              <span>Copier</span>
                            </>
                          )}
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={() => navigateToCoupon(spin.coupon_id!.code)}
                        className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-primary px-2 py-1.5 text-xs font-semibold text-primary-foreground transition active:scale-95"
                      >
                        <Ticket className="size-3.5" />
                        <span>Utiliser</span>
                      </button>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>

      {<BottomSheet open={showRulesSheet} onOpenChange={setShowRulesSheet}>
        <BottomSheetContent className="bg-card text-foreground">
          <BottomSheetTitle className="sr-only">Comment fonctionne la roue ?</BottomSheetTitle>
          <BottomSheetDescription className="sr-only">Règles et limites des tours gratuits.</BottomSheetDescription>
          <div className="mx-auto w-full max-w-[44ch]">
            <div className="mb-5 flex items-start justify-between">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-primary">Règles du jeu</p>
                <h2 className="mt-1 text-xl font-semibold tracking-tight">Comment ça fonctionne ?</h2>
              </div>
              <button
                type="button"
                onClick={() => setShowRulesSheet(false)}
                className="rounded-full p-2 text-muted-foreground hover:text-foreground"
              >
                <X className="size-5" />
              </button>
            </div>

            <div className="space-y-4 text-sm text-foreground">
              <div className="rounded-xl border border-border bg-muted p-4">
                <p className="font-semibold">1. Tour quotidien gratuit</p>
                <p className="mt-1 text-muted-foreground">Réclamez 1 tour gratuit chaque jour. Réservé aux comptes vérifiés.</p>
              </div>
              <div className="rounded-xl border border-border bg-muted p-4">
                <p className="font-semibold">2. Tours après recharge</p>
                <p className="mt-1 text-muted-foreground">
                  Chaque recharge ≥ 1000 FCFA vous donne 1 tour bonus.
                  <span className="mt-1 block font-medium text-foreground">Limite : {status.rechargeSpinsLimit ?? 3} tours par jour.</span>
                </p>
              </div>
              <div className="rounded-xl border border-border bg-muted p-4">
                <p className="font-semibold">3. Total maximum</p>
                <p className="mt-1 text-muted-foreground">
                  Vous ne pouvez jamais dépasser <span className="font-medium text-foreground">{status.totalLimit ?? 10} tours</span> en stock.
                  Utilisez vos tours avant de cumuler davantage.
                </p>
              </div>
              <div className="rounded-xl border border-border bg-muted p-4">
                <p className="font-semibold">4. Gains</p>
                <p className="mt-1 text-muted-foreground">
                  Les lots gagnés sont crédités sous forme de coupons de recharge utilisables immédiatement.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setShowRulesSheet(false)}
              className="mt-6 flex w-full items-center justify-center rounded-2xl bg-foreground py-3.5 text-sm font-semibold text-background transition active:scale-[0.98]"
            >
              J'ai compris
            </button>
          </div>
        </BottomSheetContent>
      </BottomSheet>}

      <Notification
        message={notification.message}
        type={notification.type}
        isVisible={notification.isVisible}
        onClose={hideNotification}
      />
    </motion.div>
  );
}

export default LotteryPage;
